import {
  deepClone,
  ensureArray,
  formatDateString,
  isHexColor,
  isIsoString,
  isPlainObject,
  isValidDateString,
  titleFromSlideId
} from './deckHelpers';
import { ALLOWED_BLOCK_TYPES } from './validateDeck';
import { isCodeBearingKey, placeholderPattern } from './placeholders';

const FORBIDDEN_BLOCK_TYPES = new Set(['table', 'chart', 'quote', 'code']);

const DEFAULTS = {
  format: 'boppps_lesson',
  version: '4.0.0',
  schema_type: 'full_deck',
  courseTopic: 'Course Title',
  instructorName: 'Instructor Name',
  instructorEmail: 'instructor@email.com',
  footerCourseInfo: 'Course Title',
  footerInstitution: 'Your Institution Name',
  footerCopyright: `© ${new Date().getFullYear()} All Rights Reserved`,
  showHeaderBar: true,
  schoolLogo: null,
  schoolLogoName: '',
  schoolLogoSize: 48,
  week: '1',
  bodyLayout: 'slides',
  slideHeaderMode: 'single',
  slideHeaderColor: '#09489f',
  slideHeaderColors: {},
  slideGlobalCss: '',
  acknowledgedBlankSlides: {},
  titleCardSettings: {
    title: 'Lesson Title',
    subtitle: 'Course Title'
  },
  endTitleCardSettings: {
    title: 'Questions & Discussion',
    subtitle: 'Thank you for your attention'
  },
  slideDeckName: 'Slide Deck',
  exportFileName: 'lesson_export',
  htmlExportTitle: 'Lesson Export',
  exportVersion: 1
};

const replacePlaceholderToken = (token) => {
  const inner = token.slice(1, -1).trim();
  const lowered = inner.toLowerCase();
  if (lowered.includes('email')) {
    return 'instructor@email.com';
  }
  if (lowered.includes('instructorname')) {
    return 'Instructor Name';
  }
  if (lowered.includes('course name') || lowered.includes('course title')) {
    return 'Course Title';
  }
  if (lowered.includes('institution')) {
    return 'Your Institution Name';
  }
  if (lowered.includes('instructor') || lowered.includes('your name')) {
    return 'Instructor Name';
  }
  return inner;
};

const replacePlaceholders = (value, path, repairs) => {
  if (typeof value !== 'string') return value;
  const replaced = value.replace(placeholderPattern(), (match) => replacePlaceholderToken(match));
  if (replaced !== value) {
    repairs.push({ path, action: 'replace_placeholder', detail: value });
  }
  return replaced;
};

const parseTableRows = (html) => {
  if (typeof html !== 'string') return [];
  const tableMatch = html.match(/<table[\s\S]*?>[\s\S]*?<\/table>/i);
  if (!tableMatch) return [];
  const tableHtml = tableMatch[0];
  const rowMatches = tableHtml.match(/<tr[\s\S]*?>[\s\S]*?<\/tr>/gi) || [];
  return rowMatches.map((rowHtml) => {
    const cellMatches = rowHtml.match(/<(td|th)[\s\S]*?>[\s\S]*?<\/\1>/gi) || [];
    return cellMatches.map((cellHtml) =>
      cellHtml
        .replace(/<\/?(td|th)[^>]*>/gi, '')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .trim()
    );
  });
};

const buildWidthPercent = (columnCount) => Number((100 / columnCount).toFixed(2));

const convertTableBlock = (block) => {
  const rows = parseTableRows(block.content || block.html || '');
  const columnCount = rows.length ? Math.max(...rows.map((row) => row.length)) : 0;
  if (columnCount > 1) {
    const headers = rows[0] || [];
    const bodyRows = rows.length > 1 ? rows.slice(1) : [];
    const widthPercent = buildWidthPercent(columnCount);
    const width = `${widthPercent}%`;
    const columns = Array.from({ length: columnCount }, (_, colIndex) => ({
      id: null,
      title: headers[colIndex] || `Column ${colIndex + 1}`,
      width,
      widthPercent,
      cards: (bodyRows.length ? bodyRows : [[]]).map((row, rowIndex) => ({
        id: null,
        title: `Row ${rowIndex + 1}`,
        content: `<p>${row[colIndex] || ''}</p>`,
        backgroundColor: '#eff6ff',
        textColor: '#1e3a8a',
        borderColor: '#3b82f6',
        showBorder: true
      }))
    }));
    return {
      ...block,
      type: 'multi-column-cards',
      content: '',
      columns
    };
  }

  const items = rows.map((cells, index) => ({
    id: null,
    title: cells[0] || `Row ${index + 1}`,
    content: `<p>${cells.slice(1).join(' | ') || cells[0] || `Row ${index + 1}`}</p>`,
    sideText: '',
    backgroundColor: '#eff6ff',
    textColor: '#1e3a8a',
    borderColor: '#3b82f6',
    showBorder: true
  }));

  return {
    ...block,
    type: 'stacked-cards',
    content: '',
    items
  };
};

const ensureBlockArrays = (block) => {
  block.items = ensureArray(block.items);
  block.cardItems = ensureArray(block.cardItems);
  block.stackedCardItems = ensureArray(block.stackedCardItems);
  block.accordionItems = ensureArray(block.accordionItems);
  block.questions = ensureArray(block.questions);
  return block;
};

const normalizeColumns = (block, repairs, path) => {
  if (!Array.isArray(block.columns)) {
    block.columns = [];
    repairs.push({ path: `${path}.columns`, action: 'add_columns', detail: 'Added empty columns array.' });
  }
  block.columns = block.columns.map((column, index) => {
    const next = { ...column };
    if (!next.widthPercent && typeof next.width === 'string' && next.width.endsWith('%')) {
      const parsed = Number.parseFloat(next.width);
      if (!Number.isNaN(parsed)) {
        next.widthPercent = parsed;
        repairs.push({
          path: `${path}.columns[${index}].widthPercent`,
          action: 'derive_width_percent',
          detail: `Derived widthPercent from ${next.width}`
        });
      }
    }
    if (!next.widthPercent && Number.isFinite(next.widthPercent) === false) {
      next.widthPercent = 50;
    }
    if (!next.width && Number.isFinite(next.widthPercent)) {
      next.width = `${next.widthPercent}%`;
    }
    return next;
  });
};

const padIndex = (value) => String(value).padStart(3, '0');
const SHORT_ID_PATTERN = /^(col|card|q|step|img|res|item|num)\d+$/i;

export const repairDeck = (input) => {
  const repairs = [];
  const deck = deepClone(input);

  if (!isPlainObject(deck)) {
    return { deck: deepClone(DEFAULTS), repairs: [{ path: 'root', action: 'replace', detail: 'Replaced invalid root.' }] };
  }

  if (deck.format !== DEFAULTS.format) {
    deck.format = DEFAULTS.format;
    repairs.push({ path: 'format', action: 'normalize_format', detail: DEFAULTS.format });
  }
  if (deck.version !== DEFAULTS.version) {
    deck.version = DEFAULTS.version;
    repairs.push({ path: 'version', action: 'normalize_version', detail: DEFAULTS.version });
  }
  if (deck.schema_type !== DEFAULTS.schema_type) {
    deck.schema_type = DEFAULTS.schema_type;
    repairs.push({ path: 'schema_type', action: 'normalize_schema_type', detail: DEFAULTS.schema_type });
  }
  deck.timestamp = isIsoString(deck.timestamp) ? deck.timestamp : new Date().toISOString();

  Object.entries(DEFAULTS).forEach(([key, value]) => {
    if (deck[key] === undefined || deck[key] === null || deck[key] === '') {
      deck[key] = value;
      repairs.push({ path: key, action: 'add_default', detail: String(value) });
    }
  });

  const stringFields = [
    'courseTopic',
    'instructorName',
    'instructorEmail',
    'footerCourseInfo',
    'footerInstitution',
    'footerCopyright',
    'schoolLogoName',
    'week',
    'slideGlobalCss',
    'slideDeckName',
    'exportFileName',
    'htmlExportTitle'
  ];

  stringFields.forEach((field) => {
    if (typeof deck[field] !== 'string' || deck[field].trim() === '') {
      deck[field] = DEFAULTS[field] || '';
      repairs.push({ path: field, action: 'normalize_string', detail: String(deck[field]) });
    }
  });

  if (typeof deck.exportVersion !== 'number') {
    deck.exportVersion = DEFAULTS.exportVersion;
    repairs.push({ path: 'exportVersion', action: 'normalize_number', detail: String(deck.exportVersion) });
  }

  if (deck.bodyLayout !== 'slides') {
    deck.bodyLayout = DEFAULTS.bodyLayout;
    repairs.push({ path: 'bodyLayout', action: 'normalize_layout', detail: DEFAULTS.bodyLayout });
  }

  if (deck.showHeaderBar !== true) {
    deck.showHeaderBar = true;
    repairs.push({ path: 'showHeaderBar', action: 'force_true', detail: 'showHeaderBar set to true.' });
  }

  if (!isValidDateString(deck.date)) {
    deck.date = formatDateString();
    repairs.push({ path: 'date', action: 'fix_date', detail: 'Set to today.' });
  }

  if (!isHexColor(deck.slideHeaderColor)) {
    deck.slideHeaderColor = DEFAULTS.slideHeaderColor;
    repairs.push({ path: 'slideHeaderColor', action: 'fix_color', detail: DEFAULTS.slideHeaderColor });
  }

  if (!['single', 'per-section'].includes(deck.slideHeaderMode)) {
    deck.slideHeaderMode = DEFAULTS.slideHeaderMode;
    repairs.push({ path: 'slideHeaderMode', action: 'fix_header_mode', detail: DEFAULTS.slideHeaderMode });
  }

  if (!isPlainObject(deck.slideHeaderColors)) {
    deck.slideHeaderColors = {};
    repairs.push({ path: 'slideHeaderColors', action: 'reset_object', detail: '{}' });
  }

  if (!isPlainObject(deck.titleCardSettings)) {
    deck.titleCardSettings = { ...DEFAULTS.titleCardSettings };
    repairs.push({ path: 'titleCardSettings', action: 'reset_object', detail: 'Added default title card settings.' });
  }
  if (!isPlainObject(deck.endTitleCardSettings)) {
    deck.endTitleCardSettings = { ...DEFAULTS.endTitleCardSettings };
    repairs.push({ path: 'endTitleCardSettings', action: 'reset_object', detail: 'Added default end title card settings.' });
  }
  if (typeof deck.titleCardSettings.title !== 'string') {
    deck.titleCardSettings.title = DEFAULTS.titleCardSettings.title;
    repairs.push({ path: 'titleCardSettings.title', action: 'normalize_string', detail: DEFAULTS.titleCardSettings.title });
  }
  if (typeof deck.titleCardSettings.subtitle !== 'string') {
    deck.titleCardSettings.subtitle = DEFAULTS.titleCardSettings.subtitle;
    repairs.push({ path: 'titleCardSettings.subtitle', action: 'normalize_string', detail: DEFAULTS.titleCardSettings.subtitle });
  }
  if (typeof deck.endTitleCardSettings.title !== 'string') {
    deck.endTitleCardSettings.title = DEFAULTS.endTitleCardSettings.title;
    repairs.push({ path: 'endTitleCardSettings.title', action: 'normalize_string', detail: DEFAULTS.endTitleCardSettings.title });
  }
  if (typeof deck.endTitleCardSettings.subtitle !== 'string') {
    deck.endTitleCardSettings.subtitle = DEFAULTS.endTitleCardSettings.subtitle;
    repairs.push({ path: 'endTitleCardSettings.subtitle', action: 'normalize_string', detail: DEFAULTS.endTitleCardSettings.subtitle });
  }

  if (deck.schoolLogo === '' || (deck.schoolLogo !== null && typeof deck.schoolLogo !== 'string')) {
    deck.schoolLogo = null;
    repairs.push({ path: 'schoolLogo', action: 'normalize_logo', detail: 'Reset schoolLogo to null.' });
  }
  if (typeof deck.schoolLogoName !== 'string') {
    deck.schoolLogoName = DEFAULTS.schoolLogoName;
    repairs.push({ path: 'schoolLogoName', action: 'normalize_logo_name', detail: 'Reset schoolLogoName.' });
  }
  if (typeof deck.schoolLogoSize !== 'number') {
    deck.schoolLogoSize = DEFAULTS.schoolLogoSize;
    repairs.push({ path: 'schoolLogoSize', action: 'normalize_logo_size', detail: 'Reset schoolLogoSize.' });
  }

  if (!isPlainObject(deck.sections)) {
    if (Array.isArray(deck.sections)) {
      const converted = {};
      deck.sections.forEach((section, index) => {
        const id = section?.id || `section-${index + 1}`;
        converted[id] = {
          id,
          title: section?.title || `Section ${index + 1}`,
          blocks: ensureArray(section?.blocks)
        };
      });
      deck.sections = converted;
      repairs.push({ path: 'sections', action: 'convert_array', detail: 'Converted sections array to object.' });
    } else {
      deck.sections = {};
      repairs.push({ path: 'sections', action: 'reset_object', detail: 'Initialized sections object.' });
    }
  }

  const slideIds = new Set();

  Object.entries(deck.sections).forEach(([sectionKey, sectionValue]) => {
    const section = isPlainObject(sectionValue) ? sectionValue : { id: sectionKey, title: sectionKey, blocks: [] };
    section.id = section.id || sectionKey;
    section.title = section.title || section.id;
    section.blocks = ensureArray(section.blocks);

    section.blocks = section.blocks.map((block, blockIndex) => {
      let next = isPlainObject(block) ? { ...block } : {};
      const path = `sections.${sectionKey}.blocks[${blockIndex}]`;

      if (!next.type || typeof next.type !== 'string') {
        next.type = 'text';
        repairs.push({ path: `${path}.type`, action: 'default_type', detail: 'Set to text.' });
      }

      // Rescue a custom HTML/CSS block from a deck saved before `custom-html`
      // reached ALLOWED_BLOCK_TYPES. Back then this very function rewrote the
      // type to `text` on the way OUT, so the damage is already on disk: the
      // file says `text` while still carrying the markup and styles. Fixing the
      // allowlist stopped new saves being wrecked but healed nothing already
      // written — without this, reloading last week's deck still shows an empty
      // slide with the author's layout sitting inert in the JSON.
      //
      // The signal is deliberately narrow. `html` alone would catch accordion
      // blocks, which carry a generated `html` field; only a custom block
      // writes `cssScope`, and only a real layout has markup to restore.
      if (next.type !== 'custom-html'
        && (next.cssScope === 'slide' || next.cssScope === 'deck')
        && typeof next.html === 'string'
        && next.html.trim()) {
        next.type = 'custom-html';
        repairs.push({
          path: `${path}.type`,
          action: 'restore_custom_html',
          detail: 'Recovered a custom HTML/CSS block flattened to text by an older save.'
        });
      }

      if (next.type === 'table') {
        next = convertTableBlock(next);
        repairs.push({ path: `${path}.type`, action: 'convert_table', detail: 'Converted table to supported block.' });
      } else if (FORBIDDEN_BLOCK_TYPES.has(next.type)) {
        next.type = 'text';
        next.content = typeof next.content === 'string' ? next.content : '';
        repairs.push({ path: `${path}.type`, action: 'normalize_type', detail: 'Converted forbidden type to text.' });
      } else if (!ALLOWED_BLOCK_TYPES.includes(next.type)) {
        next.type = 'text';
        repairs.push({ path: `${path}.type`, action: 'normalize_type', detail: 'Converted unsupported type to text.' });
      }

      if (!next.slideId || typeof next.slideId !== 'string') {
        next.slideId = `${section.id}-slide-0`;
        repairs.push({ path: `${path}.slideId`, action: 'add_slide_id', detail: next.slideId });
      }
      slideIds.add(next.slideId);

      if (next.type === 'divider') {
        // A divider's content must be an object rather than a string — but it
        // is not empty. `isSlideBreak` is what ends a slide, and blanking it
        // turned every generated deck into one slide per section with its
        // blocks piled on top of each other.
        const dividerContent = isPlainObject(next.content) ? next.content : {};
        next.content = {
          ...dividerContent,
          isSlideBreak: dividerContent.isSlideBreak === true,
          slideId: dividerContent.slideId || next.slideId
        };
      } else if (['text', 'heading', 'headline', 'info-box', 'warning-box', 'exercise-box', 'list'].includes(next.type)) {
        if (typeof next.content !== 'string') {
          next.content = '';
        }
      }

      if (next.type === 'gallery') {
        next.images = ensureArray(next.images);
      }

      if (next.type === 'worksheet') {
        next.questions = ensureArray(next.questions);
      }

      if (next.type === 'accordion') {
        next.accordionItems = ensureArray(next.accordionItems);
      }

      if (next.type === 'cards' || next.type === 'stacked-cards') {
        next.items = ensureArray(next.items);
      }

      if (next.type === 'multi-column-cards') {
        normalizeColumns(next, repairs, path);
      }

      next = ensureBlockArrays(next);

      return next;
    });

    deck.sections[sectionKey] = section;
  });

  if (!isPlainObject(deck.slideVisibility)) {
    deck.slideVisibility = {};
  }
  if (!isPlainObject(deck.slideNames)) {
    deck.slideNames = {};
  }

  slideIds.forEach((slideId) => {
    if (!(slideId in deck.slideVisibility)) {
      deck.slideVisibility[slideId] = true;
      repairs.push({ path: `slideVisibility.${slideId}`, action: 'add_slide_visibility', detail: 'Added missing slideVisibility entry.' });
    }
    if (!(slideId in deck.slideNames)) {
      // Empty, not a name invented from the id. `titleFromSlideId` turned
      // `section-1-slide-7` into "Section 1 Slide 7", and the slide header
      // shows the slide's name as its title — so every unnamed slide was
      // titled with its own internal id in title case. The validator wants the
      // key to exist; it does not care that the value is blank, and blank is
      // the honest answer for a slide nobody has named.
      deck.slideNames[slideId] = '';
      repairs.push({ path: `slideNames.${slideId}`, action: 'add_slide_name', detail: 'Added missing slideNames entry.' });
    }
  });

  const usedIds = new Set();
  const isInvalidId = (value) => !value || SHORT_ID_PATTERN.test(value);
  const ensureUniqueId = (baseId) => {
    if (!usedIds.has(baseId)) return baseId;
    let attempt = 1;
    let next = `${baseId}-dup-${padIndex(attempt)}`;
    while (usedIds.has(next)) {
      attempt += 1;
      next = `${baseId}-dup-${padIndex(attempt)}`;
    }
    return next;
  };
  const registerId = (target, field, path, baseId, shouldReplace) => {
    const current = typeof target[field] === 'string' ? target[field] : '';
    let next = current;
    if (shouldReplace || isInvalidId(current) || usedIds.has(current)) {
      next = ensureUniqueId(baseId);
      target[field] = next;
      repairs.push({ path, action: 'regenerate_id', detail: `Set id to ${next}` });
    }
    usedIds.add(target[field]);
  };

  Object.entries(deck.sections).forEach(([sectionKey, sectionValue]) => {
    const section = deck.sections[sectionKey];
    section.blocks.forEach((block, blockIndex) => {
      const blockPath = `sections.${sectionKey}.blocks[${blockIndex}]`;
      const blockBaseId = `${block.slideId}-${block.type}-${padIndex(blockIndex + 1)}`;
      registerId(block, 'id', `${blockPath}.id`, blockBaseId, false);
      const blockId = block.id;
      let cardCounter = 0;
      let checklistCounter = 0;
      let numberedCounter = 0;
      ensureArray(block.columns).forEach((column, columnIndex) => {
        const columnPath = `${blockPath}.columns[${columnIndex}]`;
        const columnBaseId = `${blockId}-col-${padIndex(columnIndex + 1)}`;
        registerId(column, 'id', `${columnPath}.id`, columnBaseId, !String(column.id || '').startsWith(`${blockId}-col-`));
        ensureArray(column.cards).forEach((card, cardIndex) => {
          cardCounter += 1;
          const cardPath = `${columnPath}.cards[${cardIndex}]`;
          const cardBaseId = `${blockId}-card-${padIndex(cardCounter)}`;
          registerId(card, 'id', `${cardPath}.id`, cardBaseId, !String(card.id || '').startsWith(`${blockId}-card-`));
          ensureArray(card.checklistItems).forEach((item, itemIndex) => {
            checklistCounter += 1;
            const itemBaseId = `${blockId}-item-${padIndex(checklistCounter)}`;
            registerId(item, 'id', `${cardPath}.checklistItems[${itemIndex}].id`, itemBaseId, !String(item.id || '').startsWith(`${blockId}-item-`));
          });
          ensureArray(card.numberedList).forEach((item, itemIndex) => {
            numberedCounter += 1;
            const itemBaseId = `${blockId}-item-${padIndex(numberedCounter)}`;
            registerId(item, 'id', `${cardPath}.numberedList[${itemIndex}].id`, itemBaseId, !String(item.id || '').startsWith(`${blockId}-item-`));
          });
        });
      });
      ensureArray(block.items).forEach((item, itemIndex) => {
        const itemBaseId = `${blockId}-item-${padIndex(itemIndex + 1)}`;
        registerId(item, 'id', `${blockPath}.items[${itemIndex}].id`, itemBaseId, !String(item.id || '').startsWith(`${blockId}-item-`));
      });
      ensureArray(block.accordionItems).forEach((item, itemIndex) => {
        const itemBaseId = `${blockId}-item-${padIndex(itemIndex + 1)}`;
        registerId(item, 'id', `${blockPath}.accordionItems[${itemIndex}].id`, itemBaseId, !String(item.id || '').startsWith(`${blockId}-item-`));
      });
      ensureArray(block.questions).forEach((item, itemIndex) => {
        const itemBaseId = `${blockId}-q-${padIndex(itemIndex + 1)}`;
        registerId(item, 'id', `${blockPath}.questions[${itemIndex}].id`, itemBaseId, !String(item.id || '').startsWith(`${blockId}-q-`));
      });
      ensureArray(block.images).forEach((item, itemIndex) => {
        const itemBaseId = `${blockId}-img-${padIndex(itemIndex + 1)}`;
        registerId(item, 'id', `${blockPath}.images[${itemIndex}].id`, itemBaseId, !String(item.id || '').startsWith(`${blockId}-img-`));
      });
      ensureArray(block.cardItems).forEach((item, itemIndex) => {
        const itemBaseId = `${blockId}-item-${padIndex(itemIndex + 1)}`;
        registerId(item, 'id', `${blockPath}.cardItems[${itemIndex}].id`, itemBaseId, !String(item.id || '').startsWith(`${blockId}-item-`));
      });
      ensureArray(block.stackedCardItems).forEach((item, itemIndex) => {
        const itemBaseId = `${blockId}-item-${padIndex(itemIndex + 1)}`;
        registerId(item, 'id', `${blockPath}.stackedCardItems[${itemIndex}].id`, itemBaseId, !String(item.id || '').startsWith(`${blockId}-item-`));
      });
    });
  });

  const applyPlaceholderFixes = (value, path) => {
    if (typeof value === 'string') {
      return replacePlaceholders(value, path, repairs);
    }
    if (Array.isArray(value)) {
      return value.map((item, index) => applyPlaceholderFixes(item, `${path}[${index}]`));
    }
    if (isPlainObject(value)) {
      Object.entries(value).forEach(([key, item]) => {
        // A stylesheet is not prose, and its brackets are selectors. Leaving
        // this subtree alone is what keeps a custom HTML/CSS block's styling
        // intact across a reload.
        if (isCodeBearingKey(key)) return;
        value[key] = applyPlaceholderFixes(item, `${path}.${key}`);
      });
      return value;
    }
    return value;
  };

  applyPlaceholderFixes(deck, 'root');

  return { deck, repairs };
};
